import type {
  AgentConnectionInstructionsResponse,
  AgentConnectionSummaryResponse,
} from '@contract-analyzer/api-client';
import { BotIcon, CheckIcon, ExternalLinkIcon } from 'lucide-react';
import { useRef, useState, type ReactNode } from 'react';

import { describeConnectedSince, describeSignIns } from './agent-connection-model';
import { AgentSessionPreview } from './agent-session-preview';
import { CommandBlock } from './command-block';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { cn } from '@/lib/utils';

/** The one sentence a reader needs to give their agent; the skill does the rest. */
const AGENT_PROMPT = 'Find the Contract Analyzer skill in Dojo and help me get started.';

function HeaderMark({ connected, celebrating }: { readonly connected: boolean; readonly celebrating: boolean }) {
  return (
    <span
      aria-hidden="true"
      className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-muted text-foreground ring-1 ring-foreground/10 [&_svg]:size-5"
    >
      {connected ? (
        // Keyed so the confirmation replays exactly once, at the moment of connection.
        <CheckIcon
          className={cn('text-status-success', celebrating && 'animate-ca-delight-check')}
          key={celebrating ? 'celebrating' : 'settled'}
          strokeWidth={2.5}
        />
      ) : (
        <BotIcon />
      )}
    </span>
  );
}

function Step({
  index,
  title,
  children,
}: {
  readonly index: number;
  readonly title: string;
  readonly children: ReactNode;
}) {
  return (
    <li className="grid grid-cols-[1.5rem_minmax(0,1fr)] gap-x-3 gap-y-2">
      <span
        aria-hidden="true"
        className="flex size-6 items-center justify-center rounded-full text-xs ring-1 ring-foreground/15 font-bold tabular-nums text-muted-foreground"
      >
        {index}
      </span>
      <h3 className="self-center text-sm font-bold tracking-tight">{title}</h3>
      <div className="col-start-2 flex min-w-0 flex-col gap-2">{children}</div>
    </li>
  );
}

function ExternalLink({ href, children }: { readonly href: string; readonly children: ReactNode }) {
  return (
    <a
      className="font-medium text-foreground underline underline-offset-4 hover:text-accent"
      href={href}
      rel="noopener noreferrer"
      target="_blank"
    >
      {children}
    </a>
  );
}

function InvitationBody({ instructions }: { readonly instructions: AgentConnectionInstructionsResponse }) {
  return (
    <>
      <AgentSessionPreview />

      <ol aria-label="How to connect" className="flex flex-col gap-4">
        <Step index={1} title="Ask your agent">
          <CommandBlock command={AGENT_PROMPT} kind="prompt" label="prompt" />
          <p className="text-xs text-muted-foreground">Or install the skill yourself:</p>
          <CommandBlock command={instructions.install_command} label="install command" />
          <p className="text-xs text-muted-foreground">
            New to Dojo? <ExternalLink href={instructions.dojo_setup_url}>Set up Dojo first</ExternalLink>
          </p>
        </Step>
        <Step index={2} title="Approve the sign-in">
          <p className="text-sm font-light leading-relaxed text-pretty text-muted-foreground">
            Your agent installs the Contract Analyzer CLI and asks you to approve its sign-in. This
            window confirms it.
          </p>
        </Step>
      </ol>
    </>
  );
}

function ConnectedBody({
  connection,
  instructions,
  celebrating,
  now,
}: {
  readonly connection: AgentConnectionSummaryResponse;
  readonly instructions: AgentConnectionInstructionsResponse;
  readonly celebrating: boolean;
  readonly now: Date | undefined;
}) {
  // The dialog unmounts its content when closed, so this is the moment it opened.
  const [openedAt] = useState(() => now ?? new Date());
  const connectedSince = describeConnectedSince(connection, openedAt);
  return (
    <>
      <dl
        className={cn(
          'grid grid-cols-[auto_minmax(0,1fr)] gap-x-6 gap-y-1.5 rounded-lg bg-muted/60 px-4 py-3 text-sm ring-1 ring-foreground/10',
          celebrating && 'animate-ca-enter-up-4 [animation-delay:120ms]',
        )}
      >
        {connectedSince === null ? null : (
          <>
            <dt className="text-muted-foreground">Connected since</dt>
            <dd className="font-medium tabular-nums">{connectedSince}</dd>
          </>
        )}
        <dt className="text-muted-foreground">Signed in on</dt>
        <dd className="font-medium tabular-nums">{describeSignIns(connection, openedAt)}</dd>
      </dl>

      <section aria-labelledby="agent-connection-another" className="flex flex-col gap-3">
        <h3 className="text-sm font-bold tracking-tight" id="agent-connection-another">
          Connect another computer
        </h3>
        <p className="text-sm font-light leading-relaxed text-muted-foreground">
          Install the skill there, then sign in and approve it here.
        </p>
        <CommandBlock command={instructions.install_command} label="install command" />
        <CommandBlock command={instructions.login_command} label="sign-in command" />
        <p className="text-xs text-muted-foreground">
          To sign a computer out, run <code className="font-mono">ca logout --yes</code> on it.
        </p>
      </section>
    </>
  );
}

interface AgentConnectionDialogProps {
  readonly connection: AgentConnectionSummaryResponse;
  readonly instructions: AgentConnectionInstructionsResponse;
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly onOpenAutoFocus?: () => void;
  /** The connection landed while this dialog watched; plays the confirmation once. */
  readonly celebrating?: boolean;
  readonly now?: Date;
}

export function AgentConnectionDialog({
  connection,
  instructions,
  open,
  onOpenChange,
  onOpenAutoFocus,
  celebrating = false,
  now,
}: AgentConnectionDialogProps) {
  const skillLinkRef = useRef<HTMLAnchorElement>(null);
  const doneRef = useRef<HTMLButtonElement>(null);
  const connected = connection.status === 'connected';
  const title = connected
    ? celebrating
      ? 'You’re connected'
      : 'Your agent is connected'
    : 'Let your agent do the extraction';
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="flex max-h-[min(85vh,44rem)] flex-col gap-5 sm:max-w-xl"
        onOpenAutoFocus={(event) => {
          onOpenAutoFocus?.();
          // Land on the one primary action, not on the first copy button.
          event.preventDefault();
          (connected ? doneRef.current : skillLinkRef.current)?.focus();
        }}
      >
        <DialogHeader className="flex-row items-start gap-3 text-left">
          <HeaderMark celebrating={celebrating} connected={connected} />
          <div className="flex min-w-0 flex-col gap-1">
            <DialogTitle className="text-balance">{title}</DialogTitle>
            <DialogDescription className="text-pretty">
              {connected
                ? 'Claude Code or GitHub Copilot can now run extractions for you. Ask it for the fields you need.'
                : 'Tell Claude Code or GitHub Copilot which fields you need. It runs Contract Analyzer across the whole document set and hands back an Excel file with every value linked to its source.'}
            </DialogDescription>
          </div>
        </DialogHeader>

        <p aria-live="polite" className="sr-only">
          {celebrating ? 'Your agent is connected.' : null}
        </p>

        <div className="-mx-1 flex min-h-0 flex-col gap-5 overflow-y-auto px-1">
          {connected ? (
            <ConnectedBody
              celebrating={celebrating}
              connection={connection}
              instructions={instructions}
              now={now}
            />
          ) : (
            <InvitationBody instructions={instructions} />
          )}
        </div>

        <DialogFooter>
          {connected ? (
            <>
              <Button asChild variant="ghost">
                <a href={instructions.skill_url} rel="noopener noreferrer" target="_blank">
                  Skill page
                  <ExternalLinkIcon data-icon="inline-end" />
                </a>
              </Button>
              <DialogClose asChild>
                <Button ref={doneRef} type="button">
                  Done
                </Button>
              </DialogClose>
            </>
          ) : (
            <>
              <DialogClose asChild>
                <Button type="button" variant="ghost">
                  Not now
                </Button>
              </DialogClose>
              <Button asChild>
                <a href={instructions.skill_url} ref={skillLinkRef} rel="noopener noreferrer" target="_blank">
                  Open skill page
                  <ExternalLinkIcon data-icon="inline-end" />
                </a>
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
